import type { ReactNode } from "react";
import { useEffect } from "react";
import { createPortal } from "react-dom";
import { IconButton } from "./IconButton";
import { cn } from "../utils/cn";

type DrawerProps = {
  title: string;
  children: ReactNode;
  open?: boolean;
  onClose?: () => void;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg" | "xl" | "2xl";
};

const sizeClasses = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-2xl",
  "2xl": "max-w-4xl",
};

export function Drawer({ title, children, open = true, onClose, footer, size = "md" }: DrawerProps) {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div
        className="flex-1 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Fermer"
      />
      {/* Drawer */}
      <div
        className={cn(
          "flex flex-col h-full bg-surface border-l border-border shadow-xl",
          sizeClasses[size]
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
          <div className="text-sm font-semibold text-text">{title}</div>
          {onClose && (
            <IconButton
              icon="✕"
              ariaLabel="Fermer"
              intent="ghost"
              size="sm"
              onClick={onClose}
            />
          )}
        </div>
        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 py-4">{children}</div>
        {/* Footer */}
        {footer && (
          <div className="border-t border-border px-4 py-3">{footer}</div>
        )}
      </div>
    </div>,
    document.body
  );
}
